// src/Components/Common/ReCaptcha.jsx
import React, { useRef, useCallback } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import PropTypes from "prop-types";

const ReCaptcha = React.forwardRef(
  (
    {
      onChange,
      onExpired,
      onError,
      siteKey,
      theme = "light",
      size = "normal",
      style = {},
      className = "",
      hasError = false,
      errorMessage = "",
    },
    ref
  ) => {
    // Default site key can be provided via env (CRA requires REACT_APP_ prefix)
    const defaultSiteKey =
      process.env.REACT_APP_RECAPTCHA_SITE_KEY ||
      "6Leh8b0rAAAAAI_pzkpjRvWNM-q9o5L0lWZhi9QS";
    const recaptchaRef = useRef(null);

    // Enhanced error handler
    const handleError = useCallback(
      (error) => {
        console.error("ReCaptcha Error:", error);
        if (onError) {
          onError(error);
        }
      },
      [onError]
    );

    // Enhanced expiration handler
    const handleExpired = useCallback(() => {
      console.warn("ReCaptcha expired");
      if (onExpired) {
        onExpired();
      }
    }, [onExpired]);

    // Reset function for manual reset
    const reset = useCallback(() => {
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    }, []);

    // Expose reset function to parent component
    React.useImperativeHandle(ref, () => ({
      reset,
    }));

    return (
      <div style={{ margin: "16px 0", ...style }} className={className}>
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={siteKey || defaultSiteKey}
          onChange={onChange}
          onExpired={handleExpired}
          onError={handleError}
          theme={theme}
          size={size}
          key={`recaptcha-${siteKey || defaultSiteKey}`}
        />
        {hasError && errorMessage && (
          <div
            className="text-danger mt-2"
            style={{
              fontSize: "12px",
              textAlign: "center",
              fontWeight: "500",
            }}
          >
            {errorMessage}
          </div>
        )}
      </div>
    );
  }
);

ReCaptcha.displayName = "ReCaptcha";

ReCaptcha.propTypes = {
  onChange: PropTypes.func,
  onExpired: PropTypes.func,
  onError: PropTypes.func,
  siteKey: PropTypes.string,
  theme: PropTypes.oneOf(["light", "dark"]),
  size: PropTypes.oneOf(["compact", "normal", "invisible"]),
  style: PropTypes.object,
  className: PropTypes.string,
  hasError: PropTypes.bool,
  errorMessage: PropTypes.string,
};

export default ReCaptcha;
